import { useState, useEffect, useCallback } from 'react';
import {
  useQueryClient,
  useQueries,
  keepPreviousData,
} from '@tanstack/react-query';
import type {
  LookupRow,
  LookupTabData,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { CSV_FILE_CONFIGS } from '~/app/constants/csvAllowedFields';
import { ComponentSchemaService } from '~/app/services/knowledgeBase';

// ── Types ───────────────────────────────────────────────────────────

export type LookupQueryResult = {
  rows: LookupRow[];
  meta?: { total?: number };
};

// ── Hook ────────────────────────────────────────────────────────────

export const useLookupQueries = () => {
  const queryClient = useQueryClient();

  const [paginationState, setPagination] = useState<
    Record<string, { current: number; pageSize: number; total?: number }>
  >(() => {
    const p: Record<string, { current: number; pageSize: number }> = {};
    for (const config of CSV_FILE_CONFIGS) {
      p[config.name] = { current: 1, pageSize: 10 };
    }
    return p;
  });

  const [searchTerms, setSearchTerms] = useState<Record<string, string>>(() => {
    const s: Record<string, string> = {};
    for (const config of CSV_FILE_CONFIGS) s[config.name] = '';
    return s;
  });

  // Local overrides for transfer-modal data that bypasses react-query cache
  const [localLookupOverrides, setLocalLookupOverrides] = useState<
    Record<string, LookupTabData>
  >({});

  // ── React-query: fetch all lookup configs in parallel ───────────
  const lookupQueries = useQueries({
    queries: CSV_FILE_CONFIGS.map((config) => {
      const pg = paginationState[config.name] || { current: 1, pageSize: 10 };
      const q = searchTerms[config.name] || undefined;
      return {
        queryKey: ['lookup', config.name, pg.current, pg.pageSize, q] as const,
        queryFn: (): Promise<LookupQueryResult> =>
          ComponentSchemaService.getLookupWithMeta(config.name, {
            page: pg.current,
            limit: pg.pageSize,
            q,
          }),
        placeholderData: keepPreviousData,
        staleTime: 0,
      };
    }),
  });

  // Build backwards-compatible lookups record, applying local overrides
  const lookups: Record<string, LookupTabData> = {};
  CSV_FILE_CONFIGS.forEach((config, idx) => {
    const query = lookupQueries[idx];
    if (localLookupOverrides[config.name]) {
      lookups[config.name] = localLookupOverrides[config.name];
    } else {
      lookups[config.name] = {
        rows: query.data?.rows || [],
        loading: query.isLoading,
        error: query.error ? String(query.error) : null,
      };
    }
  });

  // Convenience accessors for specific query data
  const subsystemIdx = CSV_FILE_CONFIGS.findIndex(
    (c) => c.name === 'Subsystem',
  );
  const assetTypeIdx = CSV_FILE_CONFIGS.findIndex(
    (c) => c.name === 'Asset Type',
  );
  const componentTypeIdx = CSV_FILE_CONFIGS.findIndex(
    (c) => c.name === 'Component Class',
  );
  const subsystemData = lookupQueries[subsystemIdx]?.data as
    | LookupQueryResult
    | undefined;
  const assetTypeData = lookupQueries[assetTypeIdx]?.data as
    | LookupQueryResult
    | undefined;
  const componentTypeData = lookupQueries[componentTypeIdx]?.data as
    | LookupQueryResult
    | undefined;

  // Sync pagination totals from react-query responses
  useEffect(() => {
    CSV_FILE_CONFIGS.forEach((config, idx) => {
      const meta = (lookupQueries[idx]?.data as LookupQueryResult | undefined)
        ?.meta;
      if (meta?.total !== undefined) {
        setPagination((prev) => {
          const cur = prev[config.name];
          if (cur?.total === meta.total) return prev;
          return { ...prev, [config.name]: { ...cur, total: meta.total } };
        });
      }
    });
  }, [lookupQueries]);

  // ── Actions ─────────────────────────────────────────────────────

  const handleTableChange = useCallback(
    (configName: string, page: number, pageSize: number) => {
      setPagination((prev) => ({
        ...prev,
        [configName]: { current: page, pageSize },
      }));
    },
    [],
  );

  const handleSearch = useCallback((configName: string, q: string) => {
    setSearchTerms((prev) => ({ ...prev, [configName]: q }));
    setPagination((prev) => ({
      ...prev,
      [configName]: {
        ...(prev[configName] || { current: 1, pageSize: 10 }),
        current: 1,
      },
    }));
  }, []);

  const handleRefresh = useCallback(
    (configName: string) => {
      setLocalLookupOverrides((prev) => {
        const next = { ...prev };
        delete next[configName];
        return next;
      });
      void queryClient.invalidateQueries({
        queryKey: ['lookup', configName],
        refetchType: 'active',
      });
    },
    [queryClient],
  );

  return {
    queryClient,
    lookups,
    paginationState,
    searchTerms,
    subsystemData,
    assetTypeData,
    componentTypeData,
    localLookupOverrides,
    setLocalLookupOverrides,
    setPagination,
    onPageChange: handleTableChange,
    onSearch: handleSearch,
    onRefresh: handleRefresh,
  };
};
